import { NeedsAssignedScheduleReportTable } from './tables/NeedsAssignedTable';
import { NeedsAssignedDisciplineTable } from './tables/NeedsAssignedDisciplineTable';
import { NeedsAssignedPatientTable } from './tables/NeedsAssignedPatientTable';
import { NeedsAssignedTeamTable } from './tables/NeedsAssignedTeamTable';
import { useGetSchedulingOptions } from '../filters/useGetSchedulingOptions';
import type { SieveFilterModel } from '~/util/sieve/filter';
import { DataTableFilterInjectContextProvider } from '~/shared/data-tables/query/DataTableFilterInjectContext';
import { PatientFilter, VisitDateFromFilter, VisitDateToFilter, WeekOfFilter } from '../filters/Filters';
import { convertClientDateToServerDate } from '~/util/date';
import { useQueryState } from 'nuqs';
import { NeedsAssignedAnalytics } from './NeedsAssignedAnalytics';
import { QueryDataTableFilterBar } from '~/shared/data-tables/query/QueryDataTableFilterBar';
import { FilterBar } from '~/shared/components/filter-bar/FilterBar';
import { useQueryDataTableOptions } from '~/shared/data-tables/query/useQueryDataTableOptions';
import { ButtonGroup } from '~/shared/forms/controls/base-controls/ButtonGroup';

const groupByOptions = [
	{ id: 'none', label: 'None' },
	{ id: 'patient-code', label: 'Patient' },
	{ id: 'discipline', label: 'Discipline' },
	{ id: 'team', label: 'Team' }
];

export function NeedsAssigned() {
	const { data: options } = useGetSchedulingOptions();
	const { clearSearchOptions } = useQueryDataTableOptions();
	const [selectedGrouping, setSelectedGrouping] = useQueryState('selectedGrouping', { defaultValue: 'none' });
	const currentWeek = options.weeks.find(x => x.currentWeek);
	const defaultFilters: SieveFilterModel[] =
		selectedGrouping === 'none'
			? [
					{ name: 'visitDateFrom', operator: '>=', value: convertClientDateToServerDate(currentWeek!.startDate) },
					{ name: 'visitDateTo', operator: '<=', value: convertClientDateToServerDate(currentWeek!.endDate) }
			  ]
			: [{ name: 'weekNumber', operator: '==', value: options.weeks.find(x => x.currentWeek)!.weekNumber.toString() }];

	const toggleGrouping = (newGroup: string) => {
		clearSearchOptions();
		setSelectedGrouping(newGroup);
	};

	return (
		<div className="space-y-6">
			<NeedsAssignedAnalytics />
			<DataTableFilterInjectContextProvider defaultFilters={defaultFilters}>
				<div className="mb-4 flex flex-row items-center gap-4 align-middle">
					<span className="font-semibold">Group By</span>
					<ButtonGroup
						options={groupByOptions}
						isSelected={(id: string) => selectedGrouping === id}
						onSelect={(id: string) => toggleGrouping(id)}
					/>
				</div>
				<QueryDataTableFilterBar>
					{selectedGrouping !== 'none' && (
						<FilterBar.Item label="Week Of">
							<WeekOfFilter />
						</FilterBar.Item>
					)}
					{selectedGrouping === 'patient-code' && (
						<FilterBar.Item label="Patient">
							<PatientFilter />
						</FilterBar.Item>
					)}
					{selectedGrouping === 'none' && (
						<FilterBar.Item label="Visit Date">
							<VisitDateFromFilter />
							<FilterBar.Separator />
							<VisitDateToFilter />
						</FilterBar.Item>
					)}
				</QueryDataTableFilterBar>

				{selectedGrouping === 'none' && <NeedsAssignedScheduleReportTable />}
				{selectedGrouping === 'patient-code' && <NeedsAssignedPatientTable />}
				{selectedGrouping === 'discipline' && <NeedsAssignedDisciplineTable />}

				{selectedGrouping === 'team' && <NeedsAssignedTeamTable />}
			</DataTableFilterInjectContextProvider>
		</div>
	);
}
